import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';

/**
 * The screen that appears instead of nothing.
 *
 * React unmounts the whole tree when a render throws, and with no boundary
 * anywhere in the app that showed as a grey page: no message, no way back,
 * nothing in the interface admitting anything had happened. A merchant hit it
 * while moving between screens and could only guess whether their work had
 * survived.
 *
 * Two things are non-negotiable here. The failure has to be visible, a
 * screen that fails silently is worse than one that fails loudly, and it has
 * to be escapable without the merchant knowing to press reload. Keying this
 * boundary on the route means walking to another screen clears it, so one bad
 * page never takes the session with it.
 */

type Props = {
  children: ReactNode;
  /** Shown above the message, so the merchant knows what broke. */
  title: string;
  detailLabel: string;
  retryLabel: string;
  onRetry?: () => void;
  retryHint?: string;
};

type State = { error: Error | null; where: string | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, where: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, where: null };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only place this is recoverable from in production,
    // and it is what we will ask the merchant to read back to us.
    console.error('[krevya] screen crashed', error, info.componentStack);
    /*
     * Kept for the screen as well as the console.
     *
     * A merchant on a phone cannot open a console, so "Y is not a function"
     * the whole of what this used to show, was every crash reduced to the
     * same four words. The component stack names the piece that threw even
     * after minification, and the path says which screen they were on.
     */
    this.setState({
      where: (info.componentStack ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 4)
        .join(' < '),
    });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="crash" role="alert">
        <h2 className="crash__title">{this.props.title}</h2>
        <p className="crash__hint">{this.props.retryHint}</p>
        <button
          className="btn"
          type="button"
          onClick={() => {
            this.setState({ error: null, where: null });
            this.props.onRetry?.();
          }}
        >
          {this.props.retryLabel}
        </button>
        {/*
          A second way out, for the crash that clearing state cannot fix.
          When the shell itself threw, re-rendering the same shell throws
          again, and a merchant left tapping a button that does nothing has
          no reason to believe the next tap will differ. A reload also
          replaces a stale bundle, which is the state a browser is left in
          when a deploy lands under an open tab.
        */}
        <button className="btn-ghost" type="button" onClick={() => window.location.reload()}>
          Reload the page
        </button>
        {/*
          The actual message, folded away. Merchants do not want it and the
          people who can fix it need it verbatim, a summary would lose the
          one line that identifies the bug.
        */}
        <details className="crash__details">
          <summary>{this.props.detailLabel}</summary>
          <pre className="crash__stack">
            {[
              error.message || String(error),
              // Which screen. A crash report with no location is a crash
              // report nobody can act on.
              `at ${window.location.pathname}`,
              this.state.where ? `in ${this.state.where}` : null,
            ]
              .filter(Boolean)
              .join('\n')}
          </pre>
        </details>
      </div>
    );
  }
}

/**
 * The same boundary, translated.
 *
 * A class component cannot call a hook, and a crash screen written in English
 * for a merchant working in Turkish is another small way of saying the failure
 * was not worth explaining properly.
 */
export function ScreenBoundary({
  children,
  onRetry,
  boundaryKey,
}: {
  children: ReactNode;
  onRetry?: () => void;
  boundaryKey?: string;
}) {
  const t = useT();
  return (
    <ErrorBoundary
      key={boundaryKey}
      title={t('crash.title')}
      retryHint={t('crash.hint')}
      retryLabel={t('crash.retry')}
      detailLabel={t('crash.details')}
      {...(onRetry ? { onRetry } : {})}
    >
      {children}
    </ErrorBoundary>
  );
}
